import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, Star, LogOut, Menu, X, Phone, Mail, MapPin, Clock, CheckCircle, AlertCircle, Trash2, Eye, Settings, Lock, Palette } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import ContentManagement from './ContentManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_jatex-restoration/artifacts/qt4v4w6h_325d51de-5c76-46e5-b0a3-c049759d6ef2.jpeg";

// Auth Context
const useAuth = () => {
  const [token, setToken] = useState(localStorage.getItem('jatex_admin_token'));
  const [username, setUsername] = useState(localStorage.getItem('jatex_admin_username'));
  const [name, setName] = useState(localStorage.getItem('jatex_admin_name'));

  const login = (newToken, newUsername, newName) => {
    localStorage.setItem('jatex_admin_token', newToken);
    localStorage.setItem('jatex_admin_username', newUsername);
    localStorage.setItem('jatex_admin_name', newName || newUsername);
    setToken(newToken);
    setUsername(newUsername);
    setName(newName || newUsername);
  };

  const logout = () => {
    localStorage.removeItem('jatex_admin_token');
    localStorage.removeItem('jatex_admin_username');
    localStorage.removeItem('jatex_admin_name');
    setToken(null);
    setUsername(null);
    setName(null);
  };

  return { token, username, name, login, logout, isAuthenticated: !!token };
};

// Login Page
const LoginPage = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      onLogin(response.data.token, response.data.username, response.data.name);
      toast.success(`Welcome back, ${response.data.name}!`);
    } catch (error) {
      toast.error('Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#111827] flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_LIGHT} alt="JATEX" className="h-24 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white uppercase tracking-wide">Admin Dashboard</h1>
          <p className="text-gray-400 mt-2">Sign in to manage leads and content</p>
        </div>
        
        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold uppercase tracking-wide text-gray-300 mb-2">Username</label>
                <Input 
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  placeholder="Enter username"
                  className="h-12 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold uppercase tracking-wide text-gray-300 mb-2">Password</label>
                <Input 
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  placeholder="Enter password"
                  className="h-12 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-12 bg-[#BF0D3E] hover:bg-[#8B0029] text-white font-bold uppercase tracking-wider"
                data-testid="admin-login-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <div className="text-center text-gray-500 text-sm mt-4 space-y-1">
              <p><strong>Joseph Medina:</strong> jmedina / jmedina</p>
              <p><strong>Joseph Orrell:</strong> jorrell / jorrell</p>
            </div>
          </CardContent>
        </Card>
        
        <div className="text-center mt-6">
          <Link to="/" className="text-[#007AFF] hover:underline">
            ← Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

// Settings Page
const SettingsPage = ({ token, name }) => {
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwords.current,
        new_password: passwords.new
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully!');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-[#111827] uppercase tracking-tight">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Current Password</label>
              <Input 
                type="password"
                value={passwords.current}
                onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                placeholder="Enter current password"
                className="h-12"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">New Password</label>
              <Input 
                type="password"
                value={passwords.new}
                onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                placeholder="Enter new password"
                className="h-12"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Confirm New Password</label>
              <Input 
                type="password"
                value={passwords.confirm}
                onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                placeholder="Confirm new password"
                className="h-12"
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#BF0D3E] hover:bg-[#8B0029] text-white font-bold uppercase tracking-wider"
              data-testid="change-password-btn"
            >
              {loading ? 'Updating...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Account Info</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">Logged in as: <strong className="text-[#111827]">{name}</strong></p>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, leadsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setStats(statsRes.data);
        setRecentLeads(leadsRes.data.slice(0, 5));
      } catch (error) {
        toast.error('Failed to load dashboard data');
      }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      <div>
        <h1 className="text-3xl font-bold text-[#111827] uppercase tracking-tight">Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of leads and activity</p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-[#BF0D3E]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-[#111827]">{stats?.total_leads || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-[#007AFF]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide">New Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-[#007AFF]">{stats?.new_leads || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-yellow-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide">Contacted</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-yellow-600">{stats?.contacted_leads || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide">Converted</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-green-600">{stats?.converted_leads || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Recent Leads</span>
            <Link to="/admin/leads" className="text-sm text-[#007AFF] hover:underline font-normal">View All →</Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentLeads.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No leads yet</p>
          ) : (
            <div className="space-y-4">
              {recentLeads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-sm" data-testid={`recent-lead-${lead.id}`}>
                  <div>
                    <p className="font-semibold text-[#111827]">{lead.name}</p>
                    <p className="text-sm text-gray-500">{lead.phone} • {lead.service_type}</p>
                  </div>
                  <Badge variant={lead.status === 'new' ? 'destructive' : lead.status === 'contacted' ? 'secondary' : 'default'}>
                    {lead.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Leads Management
const LeadsManagement = ({ token }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLeads();
  }, [token]);

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lead updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lead deleted');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const filteredLeads = filter === 'all' ? leads : leads.filter(l => l.status === filter);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'contacted': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'converted': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-management">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#111827] uppercase tracking-tight">Leads</h1>
          <p className="text-gray-500 mt-1">Manage customer inquiries</p>
        </div>
        
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="leads-filter-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>
      ) : filteredLeads.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No leads found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead) => (
            <Card key={lead.id} className="hover:shadow-lg transition-shadow" data-testid={`lead-card-${lead.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(lead.status)}
                      <h3 className="text-lg font-bold text-[#111827]">{lead.name}</h3>
                      <Badge variant={lead.status === 'new' ? 'destructive' : lead.status === 'contacted' ? 'secondary' : 'default'}>
                        {lead.status}
                      </Badge>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${lead.phone}`} className="hover:text-[#BF0D3E]">{lead.phone}</a>
                      </div>
                      {lead.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${lead.email}`} className="hover:text-[#007AFF]">{lead.email}</a>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span>{lead.service_type?.replace(/_/g, ' ')}</span>
                      </div>
                      {lead.address && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate">{lead.address}</span>
                        </div>
                      )}
                    </div>
                    
                    {lead.message && (
                      <p className="mt-3 text-sm text-gray-500 italic">"{lead.message}"</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Select value={lead.status} onValueChange={(status) => updateLeadStatus(lead.id, status)}>
                      <SelectTrigger className="w-[140px]" data-testid={`lead-status-select-${lead.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setSelectedLead(lead)}
                      data-testid={`lead-view-btn-${lead.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => deleteLead(lead.id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`lead-delete-btn-${lead.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>Full information for this inquiry</DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-gray-500">Name</label>
                <p className="text-lg font-semibold">{selectedLead.name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-gray-500">Phone</label>
                  <p>{selectedLead.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-500">Email</label>
                  <p>{selectedLead.email || 'N/A'}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-500">Address</label>
                <p>{selectedLead.address || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-500">Service Requested</label>
                <p>{selectedLead.service_type?.replace(/_/g, ' ')}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-500">Message</label>
                <p className="italic">{selectedLead.message || 'No message provided'}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-500">Submitted</label>
                <p>{new Date(selectedLead.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Blog Management Placeholder
const BlogManagement = () => {
  return (
    <div className="space-y-6" data-testid="blog-management">
      <div>
        <h1 className="text-3xl font-bold text-[#111827] uppercase tracking-tight">Blog</h1>
        <p className="text-gray-500 mt-1">Manage blog posts for SEO</p>
      </div>
      
      <Card>
        <CardContent className="py-16 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-[#111827] mb-2">Blog Coming Soon</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Create and manage SEO-optimized blog posts to drive organic traffic for Houston roofing keywords.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Reviews Management Placeholder
const ReviewsManagement = () => {
  return (
    <div className="space-y-6" data-testid="reviews-management">
      <div>
        <h1 className="text-3xl font-bold text-[#111827] uppercase tracking-tight">Reviews</h1>
        <p className="text-gray-500 mt-1">Manage customer testimonials</p>
      </div>
      
      <Card>
        <CardContent className="py-16 text-center">
          <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-[#111827] mb-2">Reviews Coming Soon</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Collect and display customer reviews to build trust with potential clients.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const { token, username, name, login, logout, isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  if (!isAuthenticated) {
    return <LoginPage onLogin={login} />;
  }

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/leads', icon: Users, label: 'Leads' },
    { path: '/admin/content', icon: Palette, label: 'Content' },
    { path: '/admin/blog', icon: FileText, label: 'Blog' },
    { path: '/admin/reviews', icon: Star, label: 'Reviews' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#111827] transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <img src={LOGO_LIGHT} alt="JATEX" className="h-16" />
          </div>
          
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
                  location.pathname === item.path 
                    ? 'bg-[#BF0D3E] text-white' 
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
          
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center justify-between text-gray-400 mb-4">
              <span className="text-sm">Signed in as <strong className="text-white">{name || username}</strong></span>
            </div>
            <button 
              onClick={handleLogout}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors w-full"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <button 
              className="lg:hidden p-2"
              onClick={() => setSidebarOpen(true)}
              data-testid="admin-mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-sm text-[#007AFF] hover:underline">
                View Website →
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route index element={<DashboardOverview token={token} />} />
            <Route path="leads" element={<LeadsManagement token={token} />} />
            <Route path="content" element={<ContentManagement token={token} />} />
            <Route path="blog" element={<BlogManagement />} />
            <Route path="reviews" element={<ReviewsManagement />} />
            <Route path="settings" element={<SettingsPage token={token} name={name} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
